import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ListContextProvider, useList } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { IconButton } from 'shared/mui/IconButtons';
import { PageHeader } from 'shared/mui/PageHeader';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { FunctionField } from 'shared/react-admin/Fields';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { MerchantProviderSetting } from 'types/embedded/merchantProviderSettings';

import {
  MerchantProviderSettingsRow,
  useMerchantProviderSettingsRows,
} from './hooks/useMerchantProviderSettingsRows';
import { DatagridStyled } from './MerchantsShowSettings.styled';
import { MerchantsShowSettingsCreateDialog } from './MerchantsShowSettingsCreateDialog/MerchantsShowSettingsCreateDialog';
import { MerchantsShowSettingsEditDialog } from './MerchantsShowSettingsEditDialog/MerchantsShowSettingsEditDialog';

type Props = {
  merchantId?: string;
  merchantName?: string;
};

export const MerchantsShowSettings = ({ merchantId, merchantName }: Props) => {
  const translate = useTypedTranslate();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const settingModal = useRecordModal<MerchantProviderSetting>();

  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>();

  const { rows, isPending, isLoading } = useMerchantProviderSettingsRows(merchantId);
  const listContext = useList({
    data: rows,
    isPending,
    isLoading,
    perPage: 10,
  });

  const showAlert = rows.length <= 0;

  const handleAddClick = (providerId: string) => {
    setSelectedProviderId(providerId);
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setSelectedProviderId(undefined);
    setOpenCreateDialog(false);
  };

  return (
    <>
      <PageHeader
        headerTitle={{
          titleText: translate('app.merchants.providerSettings'),
          titleSize: 'smallBold',
        }}
      />
      <div>
        {!isLoading && showAlert && (
          <Alert severity="info">
            {translate('app.merchants.providerSettingsAlert', {
              name: merchantName,
            })}
          </Alert>
        )}
        <ListContextProvider value={listContext}>
          {isLoading ? (
            <DatagridSkeleton columnsCount={2} />
          ) : (
            <DatagridStyled bulkActionButtons={false}>
              <FunctionField
                cellClassName="column-provider"
                headerClassName="column-provider"
                label="Провайдер"
                render={(record: MerchantProviderSettingsRow) => record.providerName}
              />
              <FunctionField
                cellClassName="column-percent"
                headerClassName="column-percent"
                label="Процент, %"
                render={(record: MerchantProviderSettingsRow) =>
                  record.hasSetting ? (
                    <Box alignItems="center" display="flex" gap={1}>
                      <span>{record.percent}%</span>
                      <IconButton
                        onClick={() => settingModal.handleOpenRecordModal(record.setting)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button label="Добавить" onClick={() => handleAddClick(record.id)} />
                  )
                }
              />
            </DatagridStyled>
          )}
          <Pagination />
        </ListContextProvider>
        {openCreateDialog && selectedProviderId && merchantId && (
          <MerchantsShowSettingsCreateDialog
            merchantId={merchantId}
            onClose={handleCloseCreateDialog}
            open={openCreateDialog}
            providerId={selectedProviderId}
          />
        )}
        {settingModal.openRecordModal && (
          <MerchantsShowSettingsEditDialog
            onClose={settingModal.handleCloseRecordModal}
            open={settingModal.openRecordModal}
            record={settingModal.record}
          />
        )}
      </div>
    </>
  );
};
